/**
 * tradepanel.ts — the TradingView-style bottom dock: chrome + Hyperliquid tables.
 *
 * WHAT THIS IS
 * ------------
 * TradingView's bottom panel (the strip that hosts Strategy Tester / Pine Editor /
 * Replay Trading) wrapping Hyperliquid's account tabs (Balances / Positions /
 * Open Orders / Trade History / Funding History). The chrome is TradingView's —
 * tab bar, collapse chevron, maximise, drag-to-resize; the tables are
 * Hyperliquid's columns, because that is the screen Andrei already reads.
 *
 * THE ONE RULE, INHERITED FROM main.ts
 * ------------------------------------
 * This file RENDERS. It never decides anything money-critical:
 *
 *   * no ICT rule, threshold or fib ratio (same rule as every other file in web/);
 *   * no risk verdict — DEAD_STOP / ENTRY_NO_SL and friends arrive from the
 *     `hl-review` engine via /api/account/snapshot, and are printed verbatim;
 *   * no TP/SL inference — the backend derives those from the order graph
 *     because the vendored `Position.stop_loss` is a placeholder equal to entry
 *     (measured again 2026-07-27: AMZN short reported stop=tp1=tp2=242.56=entry);
 *   * no order construction — a mutation is `{verb, symbol, params}` posted to
 *     /api/trade/plan, and the argv is built server-side from a whitelist.
 *
 * If this file ever computes a stop distance or decides a position is safe, the
 * panel has become a second source of truth about money, which is strictly worse
 * than no panel.
 *
 * THE WRITE FLOW IS TWO-CLICK BY CONSTRUCTION -- EXCEPT A CANCEL
 * ---------------------------------------------------------------
 * A `place` is DRY-RUN first (`/api/trade/plan`), which returns exec_tools' own
 * preview plus a token bound to that exact argv. The confirm button posts that
 * token to `/api/trade/execute`. A user cannot skip the preview, because without
 * a token the execute route refuses -- and the token cannot be reused for a
 * different price, because the price is inside its signature.
 *
 * THE TOKEN IS GONE FOR `close` AND `cancel`, since 2026-08-26 (card t_992b85d1),
 * and it was not merely redundant there -- it was the reason closing a position
 * FAILED. A token binds two requests separated by a HUMAN DECISION; these two send
 * inside ONE click, so it bound a request to itself. Worse, in Lambda it could not
 * verify at all: measured in CloudWatch, a plan answered 200 in 15.8s from one
 * container's log stream and its execute 400 from a DIFFERENT one, while
 * `trade_actions._secret()` mints a fresh HMAC key per PROCESS with
 * `CHARTAPP_PLAN_SECRET` unset on the deployed function. The backend names them in
 * `_NO_TOKEN_VERBS`, the frontend mirrors that as `NO_TOKEN_VERBS`, and
 * `chartapp/api/tests/test_no_token_verbs_mirror.py` asserts the two are EQUAL --
 * the previous half-fix edited only the frontend and left a button the backend
 * rejected with a 400.
 *
 * A CANCEL IS THE ONE EXCEPTION TO THE MODAL, since 2026-08-13. Its button sends on
 * ONE click with no dialog, because cancelling only ever REMOVES a resting order: it
 * cannot open a position, cannot raise exposure, and the worst case is an order Andrei
 * re-places. The allowlist is `NO_CONFIRM_VERBS` in `oneclick.ts` and it contains
 * `cancel` and nothing else: `close` realises PnL, and the modifies and `place` change
 * or create exposure.
 *
 * A CLOSE KEEPS THE MODAL AND LOST THE DRY-RUN, since 2026-08-25 (card t_21d2c005).
 * Two different questions: `NO_CONFIRM_VERBS` is "may one click send it with no modal
 * at all", `NO_DRYRUN_VERBS` is "does the modal need a preview before its Confirm".
 * `close` is the second and not the first — it realises PnL, so the deliberate press in
 * a dialog naming the symbol stays; but `manage.py::_close` runs NO risk gate, so the
 * preview it dropped was a preview of a risk-reducing act rather than the last check.
 * `place` is in NONE of the three sets: it OPENS exposure, its dry-run IS the check,
 * and its token binds that check to the confirmation a human read.
 *
 * After any confirmed action the book is RE-AUDITED (`?force=true`). exec_tools'
 * `confirmed: true` is a self-report: `cancel` has returned
 * `{"cancelled": false, "error": null}` for orders that were already gone
 * (root .hermes.md §0b.1). The panel therefore shows the re-read, not the claim —
 * which is exactly why a one-click cancel row never says "Cancelled" on a
 * response. It says it by the order VANISHING from the re-read book; until then
 * the row reads "Cancelling..." or "Still open - cancel failed". See `cancelCell`.
 */

import { type SortKeys, type SortState, sortRows, sortTh } from './sort';
// The cancel row's copy and its state names. They live in `oneclick.ts` beside the
// allowlist that lets a cancel skip the dialog, because the label and the permission
// are the same decision seen from two ends.
import { CANCEL_TEXT, type CancelRowState } from './oneclick';
// The setups-vs-book join. It lives in its own module because it is the one part of
// this tab that reasons rather than renders, and it is unit-tested without any HTML.
import { latestKeys, looseOrders, setupState, type SetupState } from './setupstate';

/** One resting order / bracket leg. `parent_oid` set = a child leg. */
export type OrderRow = {
  oid: number | null;
  parent_oid: number | null;
  coin: string;
  dex: string;
  time_ms: number | null;
  order_type: string | null;
  kind: string;
  side: string;
  direction: string;
  size: number;
  limit_px: number | null;
  trigger_px: number | null;
  trigger_condition: string | null;
  reduce_only: boolean;
  is_trigger: boolean;
  is_position_tpsl: boolean;
  order_value: number | null;
  child_count: number;
};

/** A protective leg as derived from the ORDER GRAPH (never from Position.*). */
export type ProtectionLeg = {
  oid: number | null;
  kind: string;
  order_type: string | null;
  trigger_px: number | null;
  limit_px: number | null;
  size: number | null;
  is_position_tpsl: boolean;
  trigger_condition: string | null;
};

export type Finding = {
  severity: 'CRITICAL' | 'WARN' | 'INFO';
  code: string;
  coin: string | null;
  oid: number | null;
  msg: string;
};

export type PositionRow = {
  coin: string;
  dex: string;
  side: 'long' | 'short';
  size: number;
  signed_size: number;
  position_value: number | null;
  entry_px: number | null;
  mark_px: number | null;
  unrealized_pnl: number | null;
  roe_pct: number | null;
  liq_px: number | null;
  liq_dist_pct: number | null;
  margin_used: number | null;
  leverage: number | null;
  margin_mode: string | null;
  max_leverage: number | null;
  funding_since_open: number | null;
  funding_all_time: number | null;
  protection: {
    sl: number | null;
    tp: number | null;
    sl_count: number;
    tp_count: number;
    legs: ProtectionLeg[];
    source: string;
  };
  exit_coverage: { sl_pct: number; tp_pct: number } | null;
  findings: Finding[];
};

export type FillRow = {
  tid: number | null;
  oid: number | null;
  coin: string;
  dex: string;
  time_ms: number | null;
  direction: string | null;
  side: string;
  px: number;
  size: number;
  trade_value: number | null;
  fee: number | null;
  fee_token: string | null;
  closed_pnl: number | null;
  crossed: boolean;
};

export type FundingRow = {
  time_ms: number | null;
  coin: string;
  dex: string;
  usdc: number | null;
  rate: number | null;
  size: number | null;
  n_samples: number | null;
};

export type Snapshot = {
  ts: string;
  wallet: string;
  elapsed_s: number;
  dexes_swept: string[];
  dex_errors: Record<string, string>;
  /** Non-dex read failures (spot balances, history). Kept separate from
   *  `dex_errors` so the banner's "N of M dex reads" count stays honest. */
  read_errors?: Record<string, string>;
  balances: {
    account_value: number | null;
    margin_used: number | null;
    withdrawable: number | null;
    utilization_pct: number | null;
    pools: {
      dex: string;
      account_value: number;
      margin_used: number;
      withdrawable: number | null;
      utilization_pct: number | null;
    }[];
    spot: { coin: string | null; total: number | null; hold: number | null; available: number }[];
  };
  positions: PositionRow[];
  orders: OrderRow[];
  fills: FillRow[];
  funding: FundingRow[];
  history_meta: {
    days: number;
    fills_cap_reached: boolean;
    fills_truncated: boolean;
    fills_oldest_ms: number | null;
    errors: Record<string, string>;
  };
  findings: Finding[];
  exit_code: number;
  engine: { path: string; available: boolean; sha256_16: string | null; error: string | null; selftests?: number };
  cached: boolean;
  cache_age_s: number;
};

export type AccountHealth = {
  ok: boolean;
  engine: { path: string; available: boolean; sha256_16: string | null; error: string | null; selftests?: number };
  wallet: { resolved: boolean; display: string | null; error: string | null };
  invariants: { code: string; description: string }[];
  trading: {
    trading_enabled: boolean;
    env_flag: string;
    /** WHY the write path is in its state, so the UI can explain itself rather
     *  than showing a bare "read-only" with no discoverable remedy. */
    arm_reason?: string;
    signing_key_present?: boolean;
    /** true = forced on, false = forced off, null/absent = not specified. */
    override?: boolean | null;

    plan_ttl_s: number;
    venue: string;
    verbs: { verb: string; label: string; help: string; required: string[]; optional: string[] }[];
  };
  snapshot_ttl_s: number;
};

/** What `POST /api/trade/modify` returns: ONE write, then the venue's own view.
 *
 *  A DIFFERENT SHAPE FROM `TradeRun` ON PURPOSE (2026-08-18). There is no `phase`, no
 *  `plan_token` and no `dry_run` here, because there is no dry-run stage on this path
 *  and no second request to bind a token to. Keeping the two-phase type would let a
 *  renderer test `phase === 'execute'` against a field the server never sends and print
 *  "nothing has been sent yet" over a completed write — the exact defect the `apply`
 *  member below was added to fix, so it is designed out instead of patched. */
export type ModifyRun = {
  verb: string;
  symbol: string;
  venue: string;
  /** What the caller asked for, before the venue's precision was applied. */
  requested_price: number;
  /** What was SENT and is authoritative — `price_to_precision`'s output. */
  price: number;
  confirmed: boolean;
  refused: string | null;
  result: Record<string, unknown> | null;
  detail: Record<string, unknown>;
  /** The re-read of the book, ALWAYS present. `ok: false` means the state is UNKNOWN,
   *  never "clean".
   *
   *  READ IT. This type declared the field and NOTHING consumed it (R2-5): the panel
   *  proved a write by firing a SEPARATE `tpLoad(true)`, which begins
   *  `if (!accountHealth?.ok) return;` and so can decline silently — leaving a green
   *  "SENT to the exchange" on screen with no confirmation from the book at all. The
   *  server already re-read `/info` in the same response; two sources of truth for one
   *  fact, and the UI was using the more fragile one. */
  readback: {
    ok: boolean;
    error?: string;
    note?: string;
    open_orders?: number;
    triggers?: {
      order_id: string | null; trigger_px: number | null; size: number | null;
      side: string | null; reduce_only: boolean; order_kind: string | null;
    }[];
  };
  /** WHICH order this write placed, by identity — the server's diff of the book before
   *  and after (`trade_actions._placed_order_id`). `null` = it could not be named
   *  (a failed read, no new trigger, or an ambiguous one), which the read-back renders
   *  as its own loud state. It is NOT derivable in the browser: the client's own
   *  `result.order_id` is `"unknown"` for a `waitingForTrigger` leg, and matching on
   *  `kind` instead was R4-2 (a landed rung move read "DISAGREES", a failed one read
   *  as agreement). */
  placed_order_id: string | null;
  /** Is the leg this write was moving on the book NOW? ALWAYS present (R2-4).
   *
   *  It used to be computed only on the FAILURE branch, which is the hole: `confirmed`
   *  is a self-report (§0b.1), so a successful-looking write is not a state in which
   *  the leg is known to be resting — it is a state in which nothing checked. Measured:
   *  a write that succeeded with a failing final read produced `confirmed: true`,
   *  `readback.ok: false` and NO `naked` KEY AT ALL, so the panel printed the green
   *  banner over a possibly unprotected position.
   *
   *  THREE STATES, and the renderer must keep them apart: `critical: true` = the leg is
   *  OBSERVED gone (cancel-ok / create-fail, the position is unprotected on that side);
   *  `present: true` = OBSERVED resting; `present: null` = the book could not be read,
   *  so neither claim may be printed. */
  naked?: {
    leg: string;
    present: boolean | null;
    critical: boolean;
    note?: string;
  };
};

export type TradeRun = {
  phase: 'plan' | 'execute';
  verb: string;
  symbol: string;
  dry_run: boolean;
  trading_enabled: boolean;
  plan_token?: string | null;
  expires_at?: number | null;
  ttl_s?: number;
  refused: string | null;
  confirmed?: boolean;
  /** The server's own re-read of the book after a confirmed write (R2-7).
   *
   *  `execute` used to return `bool(result.get("confirmed"))` — exec_tools' SELF-REPORT,
   *  which §0b.1 records lying — and delegate verification to the caller through the
   *  `verify_with` string below. `exec_tools.cli.readback` now runs on all eight CLI
   *  write paths and WITHDRAWS `confirmed` when the book contradicts the write, so this
   *  is the evidence for the flag beside it.
   *
   *  `verdict` is THREE-valued and `unknown` must never render as either claim (§9c):
   *  a read that FAILED proves nothing in either direction. `null` when the path did not
   *  write (a dry-run or a refusal). */
  readback?: {
    ok: boolean;
    verdict: 'confirmed' | 'contradicted' | 'unknown';
    error?: string | null;
    note?: string;
    open_orders?: number | null;
    checks?: { kind: string; verdict: string; observed?: unknown }[];
  } | null;
  verify_with?: string;
  exit_code: number;
  elapsed_s: number;
  result: Record<string, unknown> | null;
};
// NO `command` / `stdout_raw` / `stderr` / `parse_error` HERE, deliberately (card
// t_824b65c5). The backend still returns them -- they are how a developer reproduces a
// run from a log -- but the moment the TYPE carries them a renderer can print them, and
// one did: the dialog showed `uv run python -m exec_tools.cli.manage modify-tp --confirm`
// to Andrei. Absence from the type makes that a COMPILE error rather than a review note.

/** A level the panel wants drawn on the chart for the selected market. */
export type PanelLevel = { price: number; color: string; title: string; dashed: boolean };

export const TABS = [
  { id: 'positions', label: 'Positions' },
  { id: 'orders', label: 'Open Orders' },
  // THE ORDER TICKET IS NOT A TAB ANY MORE (2026-08-18, card t_ab054079). Andrei: "The pane
  // from the right brings almost no value, so, if we can clean it up and move order dialog
  // there, let's do it." It is mounted permanently in `#tk-mount` inside `<aside>`, which
  // means it is visible WHILE the positions table is -- the read-the-book-then-compose
  // workflow that put it after these two tabs no longer costs a tab switch.
  //
  // Removing the entry is what keeps ONE mount: with it here, `tpRenderBody` would render a
  // second `.tk-wrap` into `#tp-body` and `tkPatchStats`' `querySelector('.tk-preview')`
  // would patch whichever node came first in the document.
  { id: 'fills', label: 'Trade History' },
  { id: 'funding', label: 'Funding History' },
  { id: 'balances', label: 'Balances' },
  { id: 'findings', label: 'Risk Findings' },
  // The morning scan's own alerts, so a setup is CLICKED into the ticket rather than
  // retyped out of a Telegram card. Renders from `/api/setups`, not from the account
  // snapshot — like `ticket`, hence `renderTab` returns null for it and `tabCounts`
  // reports null.
  { id: 'setups', label: 'Setups' },
  // The morning stop review's own record, so "what did it advise, and did I act?" is a
  // TAB rather than a scroll back through Telegram. Renders from `/api/manage/advice`
  // crossed with the account snapshot — like `setups`, hence `renderTab` returns null
  // for it and `tabCounts` reports null.
  { id: 'advice', label: 'Trade Management' },
  // WALLET MOVED TO THE PROFILE MODAL (2026-07-31). Andrei asked for a dedicated profile view and
  // for the wallet indicator to move there too.
  //
  // The original reasoning for putting it here was that it is configuration rather than a view of
  // the book — which is precisely the argument for it NOT living in a strip of live account tabs.
  // Editing a field that accepts a withdrawal-capable signing key sat one click from the position
  // table; the profile modal is the right neighbourhood for it.
  //
  // `TabId` is derived from this list, so removing the entry makes any leftover reference a
  // COMPILE error rather than a dead tab that renders nothing.
] as const;

export type TabId = (typeof TABS)[number]['id'];

// ─────────────────────────────────────────────────────────────── formatting
/** Significant-digit price formatting.
 *
 *  One formatter for a book spanning XYZ100 at ~28,771 and VELO at ~0.0036: a
 *  fixed 2dp would print `0.00` for the latter, i.e. a price of zero. Driven off
 *  magnitude so both read correctly without the panel knowing anything about tick
 *  sizes (which belong to the exchange, not to a renderer).
 *
 *  THE LADDER MAY OVER-ALLOW BUT NEVER UNDER-ALLOW the venue's own quoting rule
 *  (`chartapp/api/price_precision.py`: 5 significant figures, decimals capped at
 *  `6 - szDecimals`), because `maximumFractionDigits` drops trailing zeros while a
 *  rung set too low drops REAL digits. Measured 2026-09-05 over 281 live HL mids
 *  (card `t_ccfe3378`, probe in the review evidence): the old `a >= 0.01 ? 5` rung
 *  clipped 44 names -- every szDecimals-0 coin between 0.01 and 0.1 (DOGE 0.087741
 *  printed `0.08774`, HBAR, ALGO, POL, SEI, PYTH ...) -- so [0.01, 0.1) now allows 6,
 *  which is what the venue quotes there, and [0.1, 1) keeps 5, which is also what the
 *  venue quotes there (five significant figures of 0.xxxxx). No other rung under-allowed. */
export const pxDp = (v: number): number => {
  const a = Math.abs(v);
  return a >= 1000 ? 2 : a >= 100 ? 3 : a >= 1 ? 4 : a >= 0.1 ? 5 : a >= 0.01 ? 6 : 8;
};

export const fmtPx = (v: number | null | undefined): string => {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  return v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: pxDp(v) });
};

export const fmtUsd = (v: number | null | undefined, dp = 2): string =>
  v === null || v === undefined || !Number.isFinite(v)
    ? '—'
    : v.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp });

/** Signed money by default; `currency = false` drops the `$` for the pane OHLC header, whose
 *  change figure is a price delta printed beside prices that carry no symbol either. Defaults
 *  TRUE so the existing money call sites do not move. */
export const fmtSigned = (v: number | null | undefined, dp = 2, currency = true): string => {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  const s = Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp });
  return `${v > 0 ? '+' : v < 0 ? '-' : ''}${currency ? '$' : ''}${s}`;
};

export const fmtSize = (v: number | null | undefined): string => {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  return v.toLocaleString('en-US', { maximumFractionDigits: 6 });
};

/** Local time, matching the HL panel's `M/D/YYYY - HH:MM:SS`. */
export const fmtTime = (ms: number | null | undefined): string => {
  if (!ms) return '—';
  const d = new Date(ms);
  return `${d.toLocaleDateString('en-US')} - ${d.toLocaleTimeString('en-US', { hour12: false })}`;
};

export const esc = (s: unknown): string =>
  String(s ?? '').replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string,
  );

/** `xyz:AMZN` -> `AMZN` + an `xyz` chip, the way the HL UI tags builder markets.
 *
 *  Rendered as a link-ish control, because clicking it charts that market. It was
 *  plain bold text identical to every other cell, so the interaction was invisible —
 *  Andrei had to draw an arrow on a screenshot to ask whether it existed. */
export const marketCell = (coin: string, dex: string, extra = ''): string => {
  const bare = coin.includes(':') ? coin.split(':')[1] : coin;
  const chip = dex && dex !== 'core' ? `<span class="chip">${esc(dex)}</span>` : '';
  return `<span class="mkt" title="click to chart ${esc(coin)}">${esc(bare)}</span>${chip}${extra}`;
};

const sideClass = (side: string): string =>
  side === 'long' || side === 'buy' || side === 'close_short' ? 'long' : 'short';

const dirLabel = (d: string | null): string =>
  ({ long: 'Long', short: 'Short', close_long: 'Close Long', close_short: 'Close Short' })[
    String(d)
  ] ?? String(d ?? '—');

/** Numbers only, right-aligned, tabular — a money column you can scan. */
const num = (html: string, cls = ''): string => `<td class="n ${cls}">${html}</td>`;

// ────────────────────────────────────────────────────────────────── tables
export function emptyState(msg: string, sub = ''): string {
  return `<div class="tp-empty"><div class="tp-empty-title">${esc(msg)}</div>${
    sub ? `<div class="tp-empty-sub">${esc(sub)}</div>` : ''
  }</div>`;
}

/**
 * The banner every tab must carry when a dex read FAILED.
 *
 * WHY THIS EXISTS — a defect caught by looking at the running app, 2026-07-27.
 * During a transient `/info` hiccup the snapshot came back with empty
 * `positions`/`orders` and populated `dex_errors`, and the panel rendered a
 * calm "No open positions". That is the one lie this app forbids: an unreadable
 * book is INDISTINGUISHABLE from a flat one, and the difference is a position
 * you think you closed versus one still carrying risk. The backend was already
 * correct (a failed read surfaces as a CRITICAL `AUDIT_INCOMPLETE` finding) —
 * the UI was throwing that away on every tab except Risk Findings.
 *
 * The rule is the same one the overlays follow: "an empty overlay is
 * indistinguishable from 'the detector found nothing', the one lie this app must
 * not tell" (chartapp/api/main.py). A table is no different.
 */
function incompleteBanner(snap: Snapshot): string {
  const dexFailed = Object.entries(snap.dex_errors ?? {});
  const otherFailed = Object.entries(snap.read_errors ?? {});
  if (!dexFailed.length && !otherFailed.length) return '';
  // The count reads "N of M DEX reads", so only dex failures may go in N. Folding
  // the wallet-unified spot read into it printed "3 of 2 dex reads FAILED" during
  // a live HTTP 429 (observed 2026-07-27) — a number that undermines the warning
  // it is attached to.
  const head = dexFailed.length
    ? `${dexFailed.length} of ${snap.dexes_swept.length} dex reads FAILED`
    : `${otherFailed.length} read(s) FAILED`;
  const detail = [...dexFailed, ...otherFailed]
    .map(([d, e]) => `${d || 'core'} (${e})`)
    .join('; ');
  return `<div class="tp-note bad">⚠ INCOMPLETE — ${head}, so this table is NOT a complete picture of the account: ${esc(
    detail,
  )}. An unreadable book can hide an open position — press refresh before acting on what you see.</div>`;
}

/** Positions — Hyperliquid's own column set, plus the panel's manage actions.
 *
 *  `TP/SL` shows the ORDER-GRAPH-derived levels with their leg counts. A `⚠`
 *  appears when a side has no leg at all: an unprotected position is the single
 *  most consequential thing this table can tell you, so it is visible in the row
 *  rather than only in the findings tab. */
export const POSITION_SORT_KEYS: SortKeys<PositionRow> = {
  // Bare tail, so `xyz:AMZN` sorts under A with the core coins rather than the
  // whole non-core dex clumping under `x`. The dex is still shown in the cell.
  coin: (p) => (p.coin.includes(':') ? p.coin.split(':')[1] : p.coin),
  size: (p) => p.signed_size,
  position_value: (p) => p.position_value,
  entry_px: (p) => p.entry_px,
  mark_px: (p) => p.mark_px,
  pnl: (p) => p.unrealized_pnl,
  liq_px: (p) => p.liq_px,
  margin_used: (p) => p.margin_used,
  funding: (p) => p.funding_since_open,
  // Unprotected positions (`sl === null`) go to one END of this column, never
  // into the middle -- see sort.ts.
  sl: (p) => p.protection.sl,
};

/**
 * THE TP/SL CELL: the position's ACTUAL RESTING PROTECTIVE ORDERS, read-only.
 *
 * Andrei, 2026-08-14: *"take a look at what Hyperliquid provides - clear limit and
 * market close, and attached SL/TP orders management"*. HL lists the attached legs with
 * their trigger conditions and a per-order cancel; this is that, from data the snapshot
 * already carries. No new endpoint, no new derivation -- `protection.legs` is produced
 * server-side by the audited `hl_check.leg_kind`.
 *
 * KIND COMES FROM THE LEG'S OWN `kind`, NEVER FROM A PRICE COMPARISON. `trigger_px`
 * above the mark means SL for a long and TP for a short; inferring it here would be
 * correct for half his book and exactly backwards for the other half, and it would
 * label a target "Stop Loss" on a screen used to decide whether a position is
 * protected. The server already decided this against the order graph; a second opinion
 * written here could only disagree with it.
 *
 * NO OID MEANS NO BUTTON, and a partly-read book means no buttons at all: `dex_errors`
 * non-empty means we could not confirm the leg still rests, and a Cancel asserts that
 * it does. It says "cannot check" in the same words the setups cell uses rather than
 * going quietly blank -- silence would read as "no protection", which is the one misread
 * this cell must not produce.
 *
 * A side with NO leg still says so loudly (the `⚠ none` that was here before): an
 * unprotected position is the most consequential thing this table can tell you.
 */
function protectionCell(p: PositionRow): string {
  const legs = p.protection.legs;
  // The price a leg fires at: its trigger for a stop/TP-market leg, else its resting
  // limit. Both are the leg's OWN fields -- no inference.
  const legPx = (l: ProtectionLeg): number | null => l.trigger_px ?? l.limit_px;
  // READ-ONLY, ON ANDREI'S INSTRUCTION (2026-08-16): "remove cancel button from positions tab in
  // TP/SL column. We have Close (Market) button in this table for the same purpose".
  //
  // The per-leg Cancel shipped in `2c57e72` and is DELETED, not hidden. He is right that this
  // column did not need it: what he wants from a position row is to EXIT, and `Close (Market)`
  // does that. Cancelling a protective leg leaves the position OPEN AND UNPROTECTED -- a
  // strictly worse outcome than closing, and the opposite of what a row action should make easy
  // (his MSFT row already reads "no SL" at -75% ROE, which is exactly that state arrived at by
  // accident). Re-arming an exit still lives one column left, in the SL and TP buttons.
  //
  // `dex_errors` needs no branch any more: with nothing to click there is no claim to withhold.
  // The legs themselves are PRESENCE facts read from the order graph, and a stale read cannot
  // invent one.
  const line = (l: ProtectionLeg): string => {
    const px = legPx(l);
    const cond = l.trigger_condition && l.trigger_condition !== 'N/A'
      ? esc(l.trigger_condition)
      : px === null ? '—' : fmtPx(px);
    return `<div class="leg"><span class="leg-kind">${esc(l.kind)}</span> ${cond}</div>`;
  };
  const missing: string[] = [];
  if (!legs.some((l) => l.kind === 'SL')) missing.push('SL');
  if (!legs.some((l) => l.kind === 'TP')) missing.push('TP');
  const warn = missing.length
    ? `<div class="bad">⚠ no ${missing.join(' / ')}</div>`
    : '';
  const cov = p.exit_coverage
    ? `<div class="${p.exit_coverage.sl_pct < 100 ? 'warn' : 'muted'}">SL ${p.exit_coverage.sl_pct}%</div>`
    : '';
  return `${legs.map(line).join('')}${warn}${cov}`;
}

export function renderPositions(snap: Snapshot, selected: string | null, sort: SortState = null): string {
  if (!snap.positions.length) {
    // A failed read must NOT render as a flat book — see incompleteBanner.
    return incompleteBanner(snap) + emptyState(
      Object.keys(snap.dex_errors ?? {}).length ? 'No positions in the dexes that COULD be read' : 'No open positions',
      'Resting entry orders appear under Open Orders.');
  }
  const rows = sortRows(snap.positions, sort, POSITION_SORT_KEYS)
    .map((p) => {
      const worst = p.findings.reduce<string | null>(
        (acc, f) => (f.severity === 'CRITICAL' ? 'CRITICAL' : acc ?? (f.severity === 'WARN' ? 'WARN' : null)),
        null,
      );
      // Liquidation distance is coloured, not just printed: it is the number that
      // decides whether the stop can fire at all.
      const liqCls = p.liq_dist_pct === null ? '' : p.liq_dist_pct < 5 ? 'bad' : p.liq_dist_pct < 10 ? 'warn' : '';
      return `
      <tr class="${sideClass(p.side)}-row ${selected === p.coin ? 'sel' : ''}" data-coin="${esc(p.coin)}">
        <td>${marketCell(p.coin, p.dex,
          `<span class="lev">${p.leverage ?? '—'}x</span>${
            worst ? `<span class="dot ${worst.toLowerCase()}" title="${esc(worst)}"></span>` : ''
          }`)}</td>
        ${num(`<span class="${sideClass(p.side)}">${p.side === 'long' ? '' : '-'}${fmtSize(p.size)}</span>`)}
        ${num(fmtUsd(p.position_value))}
        ${num(fmtPx(p.entry_px))}
        ${num(fmtPx(p.mark_px))}
        ${num(
          `<span class="${(p.unrealized_pnl ?? 0) >= 0 ? 'pos' : 'neg'}">${fmtSigned(p.unrealized_pnl)}${
            p.roe_pct === null ? '' : ` (${p.roe_pct > 0 ? '+' : ''}${p.roe_pct}%)`
          }</span>`,
        )}
        ${num(`${fmtPx(p.liq_px)}${p.liq_dist_pct === null ? '' : `<span class="muted"> ${p.liq_dist_pct}%</span>`}`, liqCls)}
        ${num(`${fmtUsd(p.margin_used)}<span class="muted"> ${esc(p.margin_mode ?? '')}</span>`)}
        ${num(`<span class="${(p.funding_since_open ?? 0) >= 0 ? 'pos' : 'neg'}">${fmtSigned(p.funding_since_open, 4)}</span>`)}
        ${num(protectionCell(p), 'prot')}
        <td class="act">
          <button class="mini" data-act="modify-sl" data-coin="${esc(p.coin)}">SL</button>
          <button class="mini" data-act="modify-tp" data-coin="${esc(p.coin)}">TP</button>
          <button class="mini danger" data-act="close-full" data-coin="${esc(p.coin)}" title="market order, reduce-only -- fills at whatever the book offers">Close (Market)</button>
        </td>

      </tr>`;
    })
    .join('');
  return `${incompleteBanner(snap)}<table class="tp-table">
    <thead><tr>
      ${sortTh('Market', 'coin', sort)}${sortTh('Size', 'size', sort, 'n')}${sortTh('Position Value', 'position_value', sort, 'n')}
      ${sortTh('Entry Price', 'entry_px', sort, 'n')}${sortTh('Mark Price', 'mark_px', sort, 'n')}${sortTh('PNL (ROE %)', 'pnl', sort, 'n')}
      ${sortTh('Liq. Price', 'liq_px', sort, 'n')}${sortTh('Margin', 'margin_used', sort, 'n')}${sortTh('Funding', 'funding', sort, 'n')}
      ${sortTh('TP / SL', 'sl', sort, 'n prot')}<th>Manage</th>
    </tr></thead><tbody>${rows}</tbody></table>`;
}

/** Open Orders. Children are indented under their parent — the API echoes each
 *  bracket leg twice (nested AND top-level) and the backend already collapsed
 *  that, so a leg appears exactly once. */
export const ORDER_SORT_KEYS: SortKeys<OrderRow> = {
  time_ms: (o) => o.time_ms,
  order_type: (o) => o.order_type,
  coin: (o) => (o.coin.includes(':') ? o.coin.split(':')[1] : o.coin),
  direction: (o) => o.direction,
  size: (o) => o.size,
  // null = a market order, which has no value to compare -- so it sorts as
  // missing, at the end, not as 0.
  order_value: (o) => o.order_value,
  limit_px: (o) => o.limit_px,
  reduce_only: (o) => (o.reduce_only ? 1 : 0),
};

/**
 * PER-ORDER CANCEL STATE, keyed by `oid` as a string -- never by row index.
 *
 * The key is the order's own identity for the same reason the button carries
 * `data-oid`: sorting reorders rows, so an index would repoint a "Cancelling..."
 * label (and, worse, a failure label) at whatever order landed in that slot.
 *
 * `cancelling` is OPTIMISTIC and says so in words ("Cancelling..."): the request is
 * out and the order is still resting until a re-read says otherwise. `failed` means
 * the order IS STILL THERE. There is no `cancelled` member on purpose -- a cancelled
 * order is proved by its ABSENCE from the next snapshot, so the row that would carry
 * that label does not exist by the time it could be shown.
 */
export type CancelStates = ReadonlyMap<string, CancelRowState>;

/**
 * The Manage cell of one order row: the Cancel button, or what the click did.
 *
 * `oid` IS THE IDENTITY. It is stamped on the button as `data-oid`, and the
 * delegated handler in `main.ts` reads it off the CLICKED button -- no index, no
 * `orders[i]`. That is the property that makes sorting safe, and this cell keeps it
 * by taking the oid rather than a row number.
 *
 * The two non-idle states BOTH mean the order is still resting:
 *
 *   * `cancelling` -- one click sent it; nothing is confirmed yet;
 *   * `failed`     -- the request did not go through, or the order is still in the
 *                     book after a forced re-read. The Cancel button COMES BACK,
 *                     because the order is still there to cancel.
 *
 * There is no success state here, and no Undo. A cancelled order is proved by
 * disappearing from the next snapshot -- so its row is gone and has nothing to say.
 * An "Undo" would have to re-place an order at a price the book may no longer offer,
 * which is a lie in a button.
 *
 * Text carries every state; the classes only reinforce it. `title` names the order
 * because the label must stay short enough not to clip at 10px.
 */
function cancelCell(oid: string, coin: string, state?: CancelRowState): string {
  const btn = `<button class="mini" data-act="cancel" data-coin="${esc(coin)}" data-oid="${oid}">Cancel</button>`;
  if (state === 'cancelling') {
    return `<span class="cancel-state pending" data-oid="${oid}" title="Sent for order ${oid}` +
      ` - still resting until the book says otherwise">${CANCEL_TEXT.cancelling}</span>`;
  }
  if (state === 'failed') {
    // The retry button is deliberately still here: the order IS open.
    return `<span class="cancel-state bad" data-oid="${oid}" title="Order ${oid} is still in the book">${
      CANCEL_TEXT.failed}</span> ${btn}`;
  }
  return btn;
}

export function renderOrders(snap: Snapshot, selected: string | null, sort: SortState = null,
                            cancelling: CancelStates = new Map()): string {
  if (!snap.orders.length) {
    return incompleteBanner(snap) + emptyState(
      Object.keys(snap.dex_errors ?? {}).length ? 'No orders in the dexes that COULD be read' : 'No open orders');
  }
  // Sorting FLATTENS the parent/child grouping -- a child leg can no longer sit
  // under its parent once rows are ordered by price. The tree glyph is kept so a
  // child is still identifiable, and the third click restores the grouping.
  const rows = sortRows(snap.orders, sort, ORDER_SORT_KEYS)
    .map((o) => {
      const isChild = o.parent_oid !== null;
      const trig = o.trigger_condition && o.trigger_condition !== 'N/A'
        ? esc(o.trigger_condition)
        : o.trigger_px
          ? `trig ${fmtPx(o.trigger_px)}`
          : '<span class="muted">—</span>';
      return `
      <tr class="${isChild ? 'child' : ''} ${selected === o.coin ? 'sel' : ''}" data-coin="${esc(o.coin)}">
        <td class="muted">${fmtTime(o.time_ms)}</td>
        <td>${isChild ? '<span class="tree">└</span>' : ''}${esc(o.order_type ?? '—')}</td>
        <td>${marketCell(o.coin, o.dex)}</td>
        <td><span class="${sideClass(o.direction)}">${dirLabel(o.direction)}</span></td>
        ${num(fmtSize(o.size))}
        ${num(o.order_value === null ? '<span class="muted">Market</span>' : fmtUsd(o.order_value))}
        ${num(o.is_trigger && !o.limit_px ? '<span class="muted">Market</span>' : fmtPx(o.limit_px))}
        <td class="${o.reduce_only ? 'muted' : ''}">${o.reduce_only ? 'Yes' : 'No'}</td>
        <td class="muted">${trig}</td>
        <td class="act">${
          o.oid === null
            ? ''
            : cancelCell(String(o.oid), o.coin, cancelling.get(String(o.oid)))
        }</td>
      </tr>`;
    })
    .join('');
  return `${incompleteBanner(snap)}<table class="tp-table">
    <thead><tr>
      ${sortTh('Time', 'time_ms', sort)}${sortTh('Type', 'order_type', sort)}${sortTh('Market', 'coin', sort)}
      ${sortTh('Direction', 'direction', sort)}${sortTh('Size', 'size', sort, 'n')}
      ${sortTh('Order Value', 'order_value', sort, 'n')}${sortTh('Price', 'limit_px', sort, 'n')}
      ${sortTh('Reduce Only', 'reduce_only', sort)}<th>Trigger</th><th></th>
    </tr></thead><tbody>${rows}</tbody></table>`;
}

export const FILL_SORT_KEYS: SortKeys<FillRow> = {
  time_ms: (f) => f.time_ms,
  coin: (f) => (f.coin.includes(':') ? f.coin.split(':')[1] : f.coin),
  direction: (f) => f.direction,
  px: (f) => f.px,
  size: (f) => f.size,
  trade_value: (f) => f.trade_value,
  fee: (f) => f.fee,
  closed_pnl: (f) => f.closed_pnl,
};

export function renderFills(snap: Snapshot, sort: SortState = null): string {
  if (!snap.fills.length) return emptyState(`No fills in the last ${snap.history_meta.days} days`);
  const rows = sortRows(snap.fills, sort, FILL_SORT_KEYS)
    .map(
      (f) => `
      <tr data-coin="${esc(f.coin)}">
        <td class="muted">${fmtTime(f.time_ms)}</td>
        <td>${marketCell(f.coin, f.dex)}</td>
        <td><span class="${String(f.direction).includes('Long') ? 'long' : 'short'}">${esc(f.direction ?? '—')}</span></td>
        ${num(fmtPx(f.px))}
        ${num(fmtSize(f.size))}
        ${num(fmtUsd(f.trade_value))}
        ${num(`<span class="muted">${fmtUsd(f.fee, 4)}</span>`)}
        ${num(`<span class="${(f.closed_pnl ?? 0) > 0 ? 'pos' : (f.closed_pnl ?? 0) < 0 ? 'neg' : 'muted'}">${fmtSigned(f.closed_pnl)}</span>`)}
      </tr>`,
    )
    .join('');
  // The truncation caveat is shown ONLY when the 2000-row cap actually cost data.
  // Measured 2026-07-27: the cap reaches back 271 days on this account, so a naive
  // "cap reached" warning would fire permanently and mean nothing.
  const caveat = snap.history_meta.fills_truncated
    ? `<div class="tp-note warn">⚠ the /info 2000-fill cap truncated this window — older fills in the ${snap.history_meta.days}d range are NOT shown</div>`
    : '';
  return `${caveat}<table class="tp-table">
    <thead><tr>
      ${sortTh('Time', 'time_ms', sort)}${sortTh('Market', 'coin', sort)}${sortTh('Direction', 'direction', sort)}
      ${sortTh('Price', 'px', sort, 'n')}${sortTh('Size', 'size', sort, 'n')}${sortTh('Trade Value', 'trade_value', sort, 'n')}
      ${sortTh('Fee', 'fee', sort, 'n')}${sortTh('Closed PNL', 'closed_pnl', sort, 'n')}
    </tr></thead><tbody>${rows}</tbody></table>`;
}

export const FUNDING_SORT_KEYS: SortKeys<FundingRow> = {
  time_ms: (f) => f.time_ms,
  coin: (f) => (f.coin.includes(':') ? f.coin.split(':')[1] : f.coin),
  usdc: (f) => f.usdc,
  rate: (f) => f.rate,
  size: (f) => f.size,
};

export function renderFunding(snap: Snapshot, sort: SortState = null): string {
  if (!snap.funding.length) return emptyState(`No funding in the last ${snap.history_meta.days} days`);
  // `total` is over the WHOLE window, not the 300 rows shown, and not over the sort.
  const total = snap.funding.reduce((a, f) => a + (f.usdc ?? 0), 0);
  // SORT BEFORE THE SLICE. Slicing first would sort only the first 300 rows, so
  // "biggest payment" would show the biggest of an arbitrary prefix -- a wrong
  // answer that looks like a right one.
  const rows = sortRows(snap.funding, sort, FUNDING_SORT_KEYS)
    .slice(0, 300)
    .map(
      (f) => `
      <tr data-coin="${esc(f.coin)}">
        <td class="muted">${fmtTime(f.time_ms)}</td>
        <td>${marketCell(f.coin, f.dex)}</td>
        ${num(`<span class="${(f.usdc ?? 0) >= 0 ? 'pos' : 'neg'}">${fmtSigned(f.usdc, 6)}</span>`)}
        ${num(f.rate === null ? '—' : `${(f.rate * 100).toFixed(6)}%`)}
        ${num(fmtSize(f.size))}
      </tr>`,
    )
    .join('');
  return `<div class="tp-note">net funding over ${snap.history_meta.days}d:
    <span class="${total >= 0 ? 'pos' : 'neg'}">${fmtSigned(total, 4)}</span>
    <span class="muted">(positive = received)</span></div>
    <table class="tp-table"><thead><tr>
      ${sortTh('Time', 'time_ms', sort)}${sortTh('Market', 'coin', sort)}${sortTh('Payment', 'usdc', sort, 'n')}
      ${sortTh('Rate', 'rate', sort, 'n')}${sortTh('Size', 'size', sort, 'n')}
    </tr></thead><tbody>${rows}</tbody></table>`;
}

/** Balances — per-dex pools FIRST, then spot.
 *
 *  Per-dex is the point: core and each HIP-3 dex are isolated clearinghouses on
 *  one wallet, so a summed "account value" hides a maxed pool behind an idle one.
 *  Empty pools are folded away to keep 10 dexes readable. */
export function renderBalances(snap: Snapshot): string {
  const b = snap.balances;
  const funded = b.pools.filter((p) => p.account_value > 0 || p.margin_used > 0);
  const idle = b.pools.length - funded.length;
  const poolRows = funded
    .map(
      (p) => `
      <tr>
        <td>${p.dex === 'core' ? '<span class="mkt">core</span>' : marketCell(p.dex, p.dex)}</td>
        ${num(fmtUsd(p.account_value))}
        ${num(fmtUsd(p.margin_used))}
        ${num(fmtUsd(p.withdrawable))}
        ${num(
          p.utilization_pct === null
            ? '—'
            : `<span class="${p.utilization_pct > 90 ? 'warn' : ''}">${p.utilization_pct}%</span>`,
        )}
      </tr>`,
    )
    .join('');
  const spotRows = b.spot
    .map(
      (s) => `
      <tr><td><span class="mkt">${esc(s.coin)}</span></td>
      ${num(fmtUsd(s.total, 4))}${num(fmtUsd(s.hold, 4))}${num(fmtUsd(s.available, 4))}<td></td></tr>`,
    )
    .join('');
  return `
  ${incompleteBanner(snap)}
  <div class="tp-note">
    perp account <b>${fmtUsd(b.account_value)}</b> · margin used ${fmtUsd(b.margin_used)}
    (${b.utilization_pct ?? '—'}%) · withdrawable ${fmtUsd(b.withdrawable)}
    <span class="muted">· ${snap.dexes_swept.length} dexes swept${idle ? `, ${idle} empty hidden` : ''}</span>
  </div>
  <table class="tp-table">
    <thead><tr><th>Perp pool (isolated clearinghouse)</th><th class="n">Account Value</th>
      <th class="n">Margin Used</th><th class="n">Withdrawable</th><th class="n">Utilisation</th></tr></thead>
    <tbody>${poolRows || '<tr><td colspan="5" class="muted">no funded perp pool</td></tr>'}</tbody>
    <thead><tr><th>Spot balance</th><th class="n">Total</th><th class="n">Hold</th>
      <th class="n">Available</th><th></th></tr></thead>
    <tbody>${spotRows || '<tr><td colspan="5" class="muted">no spot balance</td></tr>'}</tbody>
  </table>`;
}

/** Risk findings — the `hl-review` verdict, printed verbatim.
 *
 *  Not summarised and not re-worded: each row is one finding object from the
 *  audited engine. The engine digest is shown because a verdict is only as
 *  trustworthy as the code that produced it, and `hl_check.py` is a living file. */
/** Severity sorts by RANK, worst first, not alphabetically -- alphabetical would
 *  order CRITICAL, INFO, WARN and bury a WARN under the INFO rows. */
const SEV_RANK: Record<string, number> = { CRITICAL: 0, WARN: 1, INFO: 2 };

export const FINDING_SORT_KEYS: SortKeys<Finding> = {
  severity: (f) => SEV_RANK[f.severity] ?? 9,
  code: (f) => f.code,
  // Account-level findings have no coin. Null, so they group at one end.
  coin: (f) => (f.coin === null ? null : f.coin.includes(':') ? f.coin.split(':')[1] : f.coin),
  oid: (f) => f.oid,
};

export function renderFindings(snap: Snapshot, health: AccountHealth | null,
                               sort: SortState = null): string {
  const eng = snap.engine;
  const head = `<div class="tp-note">
    engine <code>${esc(eng.path.split('/').slice(-3).join('/'))}</code>
    <span class="muted">sha ${esc(eng.sha256_16 ?? '—')}${
      eng.selftests ? `, ${eng.selftests} fixture selftests` : ''
    }</span>
    · exit ${snap.exit_code} ${
      snap.exit_code === 2 ? '<span class="bad">CRITICAL</span>'
        : snap.exit_code === 1 ? '<span class="warn">WARN</span>'
        : '<span class="pos">CLEAN</span>'
    }</div>`;
  const errNote = incompleteBanner(snap);
  if (!snap.findings.length) {
    return `${head}${errNote}${emptyState('CLEAN — every invariant holds', 'Checked by the hl-review engine, not by this UI.')}`;
  }
  const byCode = new Map(health?.invariants.map((i) => [i.code, i.description]) ?? []);
  const rows = sortRows(snap.findings, sort, FINDING_SORT_KEYS)
    .map(
      (f) => `
      <tr ${f.coin ? `data-coin="${esc(f.coin)}"` : ''}>
        <td><span class="sev ${f.severity.toLowerCase()}">${f.severity}</span></td>
        <td><span class="code">${esc(f.code)}</span><div class="muted tiny">${esc(byCode.get(f.code) ?? '')}</div></td>
        <td>${f.coin ? marketCell(f.coin, f.coin.includes(':') ? f.coin.split(':')[0] : 'core') : '<span class="muted">account</span>'}</td>
        <td class="muted">${f.oid ?? ''}</td>
        <td>${esc(f.msg)}</td>
      </tr>`,
    )
    .join('');
  return `${head}${errNote}<table class="tp-table">
    <thead><tr>${sortTh('Severity', 'severity', sort)}${sortTh('Invariant', 'code', sort)}${sortTh('Market', 'coin', sort)}
    ${sortTh('Order', 'oid', sort)}<th>Detail</th></tr></thead>
    <tbody>${rows}</tbody></table>`;
}

// ──────────────────────────────────────────────────────────────────── setups
/** One row of `GET /api/setups` — an alert the morning scan actually SENT.
 *
 *  `sl` / `tp` / `r` are null TOGETHER when the row carries no archived levels, and that
 *  is deliberate on the server's side. See `renderSetups` and `levels_source`. */
export type SetupRow = {
  asset: string;
  tf: string;
  direction: string;
  /** THE SWEEP THAT MADE THIS ROW A ROW — sent for IDENTITY, never for order.
   *
   *  It is inside the table's own sort key, so it is the field that separates two alerts
   *  the rest of this payload cannot tell apart (four such pairs in the 87 prod rows).
   *  `setupKey` reads it; nothing sorts on it — `sent_at` is the recency axis
   *  (`docs/decisions/setups-review-flow.md` §3) and the two disagree on every prod row. */
  idm_swept_time: number | null;
  zone_kind: string | null;
  entry: number | null;
  sl: number | null;
  tp: number | null;
  r: number | null;
  /** WHERE `sl`/`tp` CAME FROM. `'sent'` = the writer archived them, so they ARE the card's
   *  numbers. `'not_recorded'` = the row predates 2026-08-09 and no stop was ever archived,
   *  so there is nothing to show AND nothing may be derived — the stop rule changed under
   *  the archive on 2026-08-15, and re-deriving answered "what would we advise today?" in
   *  the column that reads as "what did we advise?" (p50 2.462% / max 15.959% off the card's
   *  stop over the 41 affected prod rows; `chartapp/api/setups.py` owns the measurement).
   *
   *  OPTIONAL because an older deployment's payload does not carry it. Absent means the
   *  reason is unknown, and `levelNote` then says nothing rather than guessing one. */
  levels_source?: 'sent' | 'not_recorded' | null;
  spot_at_send: number | null;
  dist_atr_at_send: number | null;
  sent_at: number | null;
};

export type SetupsPayload = {
  available: boolean;
  reason?: string | null;
  setups: SetupRow[];
  /** How many rows the table HOLDS, which is not always how many are in `setups`. */
  total?: number;
  /** `true` when `setups` is shorter than `total`. Rendered as a sentence, never swallowed. */
  truncated?: boolean;
};

/** The sentence a truncated list owes the reader.
 *
 *  THIS IS THE WHOLE POINT OF THE 2026-08-18 FIX. The tab used to ask for 40 rows out of
 *  87, so a real Telegram-sent `xyz:BABA` alert sat at newest-first index 41 and was
 *  simply not on screen -- and nothing on screen said a row had been dropped, so the only
 *  available reading was "the scan never sent it". A list that is short for a reason the
 *  reader cannot see is worse than no list.
 *
 *  Empty string when nothing is omitted, so the caller renders nothing rather than a
 *  reassurance nobody asked for. `truncated` is trusted only alongside a `total` that can
 *  support the arithmetic; an old payload with neither field disclosed nothing and this
 *  says nothing about it rather than inventing a count. */
export function truncationNote(payload: SetupsPayload): string {
  if (!payload.truncated || typeof payload.total !== 'number') return '';
  const hidden = payload.total - payload.setups.length;
  if (hidden <= 0) return '';
  return `Showing ${payload.setups.length} of ${payload.total} sent alerts — `
    + `${hidden} older setup${hidden === 1 ? '' : 's'} ${hidden === 1 ? 'is' : 'are'} not shown.`;
}

/** "no stop advised" / "no target advised" — an ABSENCE, never a number.
 *
 *  This is the one cell in this feature that can do real damage. The server sends
 *  `sl: null` when the row carries no archived stop -- for the pre-2026-08-09 rows,
 *  `levels_source: 'not_recorded'`, i.e. no stop was ever archived for that card (NOT a
 *  failed ATR recovery: those rows' ATR is recoverable, the server simply refuses to
 *  re-derive a level with today's rule, card t_7d38d636). Every
 *  plausible-looking placeholder is a lie a tired reader would act on: `0` is a
 *  price, `—` sits in a price column and reads as one, and a blank cell reads as
 *  "nothing to worry about". So the cell says, in words, that there is no level. */
const noLevel = (what: string): string =>
  `<span class="muted">no ${esc(what)} advised</span>`;

/** The absence cell for a LEVEL, which owes the reader the reason as well as the fact.
 *
 *  TWO DIFFERENT ABSENCES, and printing one over the other is how this feature misled
 *  Andrei. `levels_source: 'not_recorded'` means the row was written before the scan
 *  archived its levels (pre-2026-08-09), so no stop was ever recorded FOR THIS CARD —
 *  which is a fact about the archive, not about the setup. Until 2026-08-21 the server
 *  filled that hole by re-deriving a stop with TODAY's rule, and the rule had changed
 *  (1 xATR -> structural, 870ca25): the recomputed stop sat p50 2.462% / max 15.959%
 *  from the one on the Telegram card, and the R beside it read 0.733..7.133 against the
 *  card's flat 1:2.00. So the number is gone and the reason is on screen instead.
 *
 *  Anything else — a row whose levels the scan itself omitted — keeps `noLevel`'s wording,
 *  which is a claim about the setup. An UNKNOWN reason (an older payload with no
 *  `levels_source`) also falls here: it says the level is absent and claims nothing about
 *  why, because inventing a reason is the same mistake one column to the left. */
const levelCell = (row: SetupRow, what: string): string =>
  row.levels_source === 'not_recorded'
    ? `<span class="muted">not recorded</span>`
    : noLevel(what);

/** How far the target is from the entry, and the R it was framed at.
 *
 *  A DOLLAR figure is deliberately NOT computed here. A dollar needs account equity
 *  and the position size, and size is derived by the server inside
 *  `POST /api/trade/bracket` from the stop — it is not a number a table row knows.
 *  Reproducing that formula here would be a second source of truth for money, so the
 *  table states what is true of the SETUP (distance, percent, R) and the dollar risk
 *  and reward appear in the order ticket the moment a row is loaded into it. */
const profitCell = (row: SetupRow): string => {
  if (row.entry === null || row.tp === null) return levelCell(row, 'target');
  const move = Math.abs(row.tp - row.entry);
  const pct = row.entry === 0 ? null : (move / Math.abs(row.entry)) * 100;
  const r = row.r === null ? '' : ` <span class="muted">at ${row.r.toFixed(1)}R</span>`;
  return `+${fmtPx(move)}${pct === null ? '' : ` <span class="muted">(${pct.toFixed(1)}%)</span>`}${r}`;
};

/** A usable epoch-second send time, or null. `0` is NOT one.
 *
 *  The table's `sent_at` is either a real send time or absent, and absent has reached this
 *  code as `0` as well as `null` (`setups.py:315` sorts nulls last with `or 0`). Zero is
 *  1970, so a bare finiteness check prints `20441d ago` / `First sent 1970-01-01` — a
 *  fabricated date wearing the scan's authority, which is the `noLevel` mistake in the time
 *  column. Both the relative phrase and the day separator route through here. */
const sentTime = (sent: number | null): number | null =>
  sent === null || !Number.isFinite(sent) || sent <= 0 ? null : sent;

/** How long ago the scan sent this setup, in words. */
const sentAgo = (sent: number | null, now = Date.now()): string => {
  const at = sentTime(sent);
  if (at === null) return '';
  const mins = Math.max(0, Math.round((now - at * 1000) / 60000));
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  return hours < 48 ? `${hours}h ago` : `${Math.round(hours / 24)}d ago`;
};

/** The UTC day a row was first sent, as a key to break the list on. `''` when unknown.
 *
 *  UTC, not local: the scan writes `sent_at` in UTC and the tests must not change verdict
 *  with the runner's timezone. The morning send lands ~11:06 UTC / 07:06 New York, so the
 *  two readings agree on the day for every row prod holds today. */
const sentDayKey = (sent: number | null): string => {
  const at = sentTime(sent);
  return at === null ? '' : new Date(at * 1000).toISOString().slice(0, 10);
};

/** The words on a day separator, and they are load-bearing.
 *
 *  IT SAYS "FIRST SENT", NEVER "TODAY". `alert_log.record_sent` writes with
 *  `attribute_not_exists(sent_at)` keyed on the SETUP, so a re-sent card keeps its original
 *  date and no send log exists to correct it: on 2026-08-18 prod wrote 5 rows and REFUSED 20
 *  duplicates, two of them the `xyz:BABA` that WAS Telegrammed that morning while still
 *  carrying its 08-13 date. A separator reading "Today" would therefore assert a send this
 *  table cannot see — the same lie `noLevel` refuses to tell about a price. */
export function sentDayLabel(sent: number | null): string {
  const key = sentDayKey(sent);
  return key ? `First sent ${key}` : 'Send date unknown';
}

/** The scan's recent setups, one click from the order ticket.
 *
 *  Andrei, 2026-08-08: *"show ticker, entry, SL, TP, potential profit calc... It
 *  might be a better UX than copy-pasting it from telegram."* Clicking a row charts
 *  that market on all three panes and loads its prices into the ticket, so the next
 *  action is a DRAG or a PRESS — never a retype. Handled in main.ts, which owns the
 *  chart and the ticket; this module only renders.
 *
 *  The asset is printed byte for byte. `xyz:AAPL` and `XYZ:AAPL` are different rows
 *  in the table the scan wrote, so nothing here normalises the spelling.
 *
 *  `cursorAt` is the index of the row he last OPENED (resolved by `resolveCursor`), or
 *  null. That row is marked `at-cursor` and carries `data-cursor`, which is what the
 *  controller scrolls to on the way back -- landing on row 0 of 87 after reading row 43
 *  is the failure this exists to prevent. */
export function renderSetups(payload: SetupsPayload | null, err: string | null = null,
                             scanNote: string | null = null,
                             snap: Snapshot | null = null,
                             cancelling: CancelStates = new Map(),
                             cursorAt: number | null = null): string {
  const head = `<div class="tp-note">
    <button class="mini" id="tp-scan" title="run the daily scan now">Refresh setups</button>
    ${scanNote ? `<span class="muted">${esc(scanNote)}</span>` : ''}
  </div>`;
  if (err) return `${head}${emptyState('The setups could not be read', err)}`;
  if (!payload) return `${head}${emptyState('No setups yet', 'Reading the daily scan…')}`;
  if (!payload.available) {
    return `${head}${emptyState('The daily scan is not connected here',
      payload.reason ?? 'Setups appear once this app is wired to the scan.')}`;
  }
  if (!payload.setups.length) {
    // THE EMPTY TABLE ASSERTS NOTHING ABOUT A SCAN. It used to read "The last scan
    // found no market close enough to trade", which is a statement about a scan HAVING
    // RUN -- and `GET /api/setups` does not carry that fact. The same table is empty
    // when no scan has ever run here, when one is running right now, and when one just
    // failed, so three different worlds were all being reported as the same completed,
    // uneventful scan. Andrei, 2026-08-13: *"when i pushed refresh setups it did not
    // show setups in the table view."*
    //
    // This is the chart rule applied to prose: a level must never assert a price it is
    // not drawn at, and a status line must never assert a scan it cannot see. When a
    // scan IS observable the caller passes `scanNote`, which says what is true; the
    // subtitle then defers to it rather than printing a competing guess beside it.
    return `${head}${emptyState('No setups in the table',
      scanNote ? '' : 'Press Refresh setups to run a scan now — it takes about 20 minutes.')}`;
  }
  // THE LATEST ROW PER ASSET, computed once for the whole table rather than per row.
  // Superseded rows stay VISIBLE and go `quiet` -- see `latestKeys` for why deleting
  // the scan's own history from the only place it is shown would be worse.
  const latest = latestKeys(payload.setups);
  const rows = payload.setups
    .map((s, i) => {
      // A DAY SEPARATOR, NOT A GROUPING. Andrei, 2026-08-18: he could not tell "which setup
      // is newer and which one is older" in a column that reads `5m ago … 5m ago … 7d ago`.
      // The list stays FLAT and stays in the server's newest-first order (`setups.py:315`):
      // this emits one extra `<tr>` before the first row of each new day, so no setup row is
      // folded, hidden behind a disclosure, or rendered twice -- the reachability constraint
      // t_b1ffc409 hands over. Asset grouping was ruled out for destroying the one axis he
      // asked to see (`docs/decisions/setups-review-flow.md`), and there is no view toggle.
      const prev = i === 0 ? null : payload.setups[i - 1];
      const sep = prev !== null && sentDayKey(prev.sent_at) === sentDayKey(s.sent_at) ? '' : `
      <tr class="daysep"><td colspan="8">${esc(sentDayLabel(s.sent_at))}</td></tr>`;
      const dex = s.asset && s.asset.includes(':') ? s.asset.split(':')[0] : 'core';
      const long = s.direction === 'bullish';
      const st = setupState(s, snap);
      const superseded = !latest.has(i);
      // Two independent facts, two independent classes: `quiet` says "an older row for
      // this market exists", the state class says what the book is doing. A superseded
      // row that needs checking must still be able to say so.
      const cls = [superseded ? 'quiet' : '', st.kind === 'none' ? '' : `st-${st.kind}`,
        i === cursorAt ? 'at-cursor' : '']
        .filter(Boolean).join(' ');
      return `${sep}
      <tr data-setup="1" data-coin="${esc(s.asset)}" data-idx="${i}"${i === cursorAt ? ' data-cursor="1"' : ''}
          data-entry="${s.entry ?? ''}" data-sl="${s.sl ?? ''}" data-tp="${s.tp ?? ''}"
          data-dir="${esc(s.direction)}"${cls ? ` class="${cls}"` : ''}>
        <td>${marketCell(s.asset, dex, `<span class="muted tiny"> ${esc(s.tf)}</span>`)}</td>
        <td><span class="side ${long ? 'long' : 'short'}">${long ? 'Long' : 'Short'}</span>
            <div class="muted tiny">${esc(sentAgo(s.sent_at))}</div></td>
        ${num(s.entry === null ? levelCell(s, 'entry') : fmtPx(s.entry))}
        ${num(s.sl === null ? levelCell(s, 'stop') : fmtPx(s.sl))}
        ${num(s.tp === null ? levelCell(s, 'target') : fmtPx(s.tp))}
        ${num(profitCell(s), 'pos')}
        <td class="act ${st.kind}">${esc(st.text)}${superseded
          ? `${st.text ? ' ' : ''}<span class="muted tiny">newer setup below</span>` : ''}</td>
        <td class="act">${setupCancelCell(st, cancelling)}</td>
      </tr>`;
    })
    .join('');
  // THE OMISSION IS ON SCREEN OR THERE IS NO OMISSION. Rendered above the table, so it is
  // read before the list is believed to be complete.
  const note = truncationNote(payload);
  return `${head}${note ? `<div class="tp-note"><span class="muted">${esc(note)}</span></div>` : ''}<table class="tp-table">
    <thead><tr><th>Market</th><th>Direction</th><th class="n">Entry</th><th class="n">Stop</th>
      <th class="n">Target</th><th class="n">If it works</th><th>Your book</th><th></th></tr></thead>
    <tbody>${rows}</tbody></table>${renderLooseOrders(payload.setups, snap)}`;
}

/**
 * THE CANCEL CELL OF A SETUP ROW -- empty on all but the armed ones.
 *
 * Andrei, 2026-08-14: *"I will check order cancellation as soon as you add this button
 * to the list of setups"*, with the constraint that shapes the whole cell: *"most of
 * my deals are in position and I don't want to cancel the micrometer"* -- he does not
 * want to cancel the WRONG order. His book as this shipped: 92 open orders, of which
 * 53 TP and 30 SL. *** 83 OF 92 ARE PROTECTION ON 28 LIVE POSITIONS. *** Cancelling
 * one silently removes a stop from a real leveraged trade, and it looks like nothing
 * happened until the market moves.
 *
 * SO THE BUTTON IS GATED ON `kind === 'armed'` AND ON NOTHING ELSE. That is not a
 * cosmetic check: `armed` is reached only via `armsSetup`, which is behind
 * `isRestingEntry` (`!reduce_only && !is_trigger && a real limit price`), and
 * `reduce_only` / `is_trigger` are precisely what make an order a TP or an SL. So
 * there is no reduce-only order in his book that can grow this button, by
 * construction rather than by a second opinion written here.
 *
 * `on_track` IS THE ONE THAT LOOKS LIKE IT SHOULD HAVE ONE AND MUST NOT. It means a
 * POSITION agrees with the setup -- there is no resting entry to cancel, and the only
 * cancellable orders on that market are the stop and target guarding it. A Cancel
 * button on an `on_track` row would point at exactly the 83. `check`, `none` and
 * `unknown` have nothing to act on either.
 *
 * AND NO OID MEANS NO BUTTON, whatever the kind. `setupState` withholds the oid when
 * `dex_errors` is non-empty, because the button asserts the order is still resting and
 * a partly-read book cannot support that. The cell says so in the same words the rest
 * of the feature uses rather than going quietly blank.
 *
 * The button itself, and the optimistic `Cancelling...` / `Still open` states, are
 * `cancelCell`'s -- the same markup and the same one-click path the Open Orders tab
 * uses. Nothing new is invented here: no modal, no dry-run, and NO SUCCESS STATE (a
 * cancel is proved by the order leaving the re-read book, never by a self-report).
 */
function setupCancelCell(st: SetupState, cancelling: CancelStates): string {
  if (st.kind !== 'armed') return '';
  if (st.oid === null || st.oid === undefined || !st.coin) {
    return '<span class="muted tiny">cannot check</span>';
  }
  const oid = String(st.oid);
  return cancelCell(oid, st.coin, cancelling.get(oid));
}

/**
 * The INVERSE JOIN, rendered under the table: resting entry orders no setup justifies.
 *
 * Andrei: *"I would like to see armed trades that are not a part of any setups
 * reported after scan (candidates for cancelation)"*. CANDIDATES. This lists them and
 * clicking one charts its market; there is no cancel control here and no code path to
 * one, which is deliberate -- the decision to pull a resting order is his.
 *
 * NOTHING IS PRINTED WHEN THERE IS NOTHING TO SAY, including when the account has not
 * loaded: an empty "no candidates" panel would assert that his orders were checked
 * against the scan, and before the snapshot arrives they were not.
 */
function renderLooseOrders(setups: SetupRow[], snap: Snapshot | null): string {
  const loose = looseOrders(setups, snap);
  if (!loose.length) return '';
  const rows = loose
    .map(({ order, kind, text }) => {
      const dex = order.coin.includes(':') ? order.coin.split(':')[0] : 'core';
      return `
      <tr data-loose="1" data-coin="${esc(order.coin)}" class="st-${kind}">
        <td>${marketCell(order.coin, dex,
          `<span class="muted tiny"> ${esc(order.order_type ?? '')}</span>`)}</td>
        <td><span class="side ${order.side === 'buy' ? 'long' : 'short'}">${
          order.side === 'buy' ? 'Long' : 'Short'}</span></td>
        ${num(fmtPx(order.limit_px))}
        ${num(fmtSize(order.size))}
        <td class="act ${kind}">${esc(text)}</td>
      </tr>`;
    })
    .join('');
  return `<div class="tp-note">Orders you have resting that no setup above accounts
    for. Nothing here has been cancelled -- this is a list to look at.</div>
    <table class="tp-table">
      <thead><tr><th>Market</th><th>Direction</th><th class="n">Price</th>
        <th class="n">Size</th><th>Why it is here</th></tr></thead>
      <tbody>${rows}</tbody></table>`;
}

/** A leg's size for a CHART LABEL — parenthesised, or dropped when it is zero.
 *
 *  `SL 0 273` (bare `fmtSize` + price) reads as the decimal `SL 0.273`, which for a
 *  stop label is a genuinely dangerous misread. And a `0` size is not a quantity at
 *  all: it is HL's exchange-managed full-position TPSL marker (`isPositionTpsl` with
 *  `sz=0`), so naming it "0" states the opposite of what it means. */
const legSize = (size: number | null | undefined): string =>
  size === null || size === undefined || size === 0 ? '(full)' : `(${fmtSize(size)})`;

/** The chart levels for one market: entry, mark, liq, TP/SL legs, resting orders.
 *
 *  Returned as data for main.ts to draw — this module never touches the chart, which
 *  is what stops two places fighting over the same level list.
 *
 *  `title` LEADS WITH WHAT THE LEVEL IS — `ENTRY` / `SL` / `TP` / `LIQ` — because
 *  that is the question being asked of the chart ("which line is my stop?"), and
 *  colour alone was answering it: three unlabelled dotted lines with correct prices
 *  and no identity (reported against the live xyz:TSLA bracket, 2026-07-27).
 *
 *  The classification is NOT re-derived here. `o.kind` comes from the audited
 *  `hl_check.leg_kind` (non-reduce-only = ENTRY, stop trigger = SL, otherwise TP),
 *  so a second opinion about which leg is a stop cannot drift from the engine that
 *  polices it. The exchange's own order type rides along in parentheses because
 *  "Stop Market" vs "Limit" is how the same row reads in the table below. */
export function levelsFor(snap: Snapshot, coin: string): PanelLevel[] {
  const out: PanelLevel[] = [];
  const push = (price: number | null | undefined, color: string, title: string, dashed = true) => {
    if (price !== null && price !== undefined && Number.isFinite(price) && price > 0) {
      // DEDUPE BY PRICE. A position's protection legs and the resting orders that
      // implement them are the SAME orders seen twice — the position's
      // `protection.legs` are derived from the order graph, and the reduce-only rows
      // in `snap.orders` are those very rows. Measured on the live xyz:AMZN short:
      // every leg appeared twice ("SL 0" + "SL (Stop Market) 0" at 273, "TP 0.824" +
      // "TP (Limit) 0.824" at 225), so each plate was painted over its own twin and
      // several labels were unreadable. First writer wins, which is the
      // position-leg pass — it carries the size the position actually protects.
      if (out.some((l) => Math.abs(l.price - price) < 1e-9)) return;
      out.push({ price, color, title, dashed });
    }
  };

  for (const p of snap.positions.filter((x) => x.coin === coin)) {
    push(p.entry_px, '#e0e3eb', `ENTRY ${p.side}`, false);
    push(p.liq_px, '#ff5252', 'LIQ');
    for (const leg of p.protection.legs) {
      const lvl = leg.trigger_px ?? leg.limit_px;
      push(lvl, leg.kind === 'SL' ? '#ef5350' : '#26a69a', `${leg.kind} ${legSize(leg.size)}`);
    }
  }
  for (const o of snap.orders.filter((x) => x.coin === coin)) {
    const lvl = o.trigger_px ?? o.limit_px;
    const color = o.reduce_only ? (o.kind === 'SL' ? '#ef5350' : '#26a69a') : '#2962ff';
    const type = o.order_type && o.order_type !== o.kind ? ` (${o.order_type})` : '';
    push(lvl, color, `${o.kind}${type} ${legSize(o.size)}`);
  }
  return out;
}

/** The write's outcome, rendered for a human.
 *
 *  The RULE verdicts are surfaced explicitly (rather than buried in raw JSON)
 *  because they are what make a stop move safe or dead: RULE 5 is
 *  stop-vs-liquidation, RULE 3 is risk-to-stop, RULE 11 is the TP side test.
 *
 *  NO SHELL COMMAND, NO SUBPROCESS OUTPUT (card t_824b65c5). This used to end in a
 *  `<details>` reading `exec_tools raw (uv run python -m exec_tools.cli.manage
 *  modify-tp --confirm)` over the CLI's stdout/stderr. Andrei saw it in his own
 *  screenshots: a shell command line is not information a trader can act on, and it
 *  advertises an implementation detail as though it were the result. The evidence that
 *  matters is the venue's own read-back (`renderReadback`), not our argv.
 *
 *  TWO-PHASE VERBS ONLY (`close` / `cancel` / `place`) since 2026-08-18. A stop or
 *  take-profit move returns a `ModifyRun` and is rendered by `renderModifyRun`, because
 *  the two shapes disagree about what exists: there is no `plan` line, no
 *  `prior_stop_preview` and no `phase` on a single-request write. */
export function renderTradeRun(run: TradeRun): string {
  const d = (run.result?.detail ?? {}) as Record<string, any>;
  const rows: [string, string][] = [];
  if (d.plan) rows.push(['plan', String(d.plan)]);
  if (d.stop_change) {
    rows.push([
      'stop',
      `${fmtPx(d.stop_change.from)} → ${fmtPx(d.stop_change.to)} <span class="muted">(baseline: ${esc(
        d.stop_change.from_source ?? '?',
      )})</span>`,
    ]);
  }
  if (d.tp_change) rows.push(['take profit', `→ ${fmtPx(d.tp_change.to)}`]);
  if (d.close) {
    rows.push(['close', `${d.close.mode} ${fmtSize(d.close.size)} (${d.close.pct_of_position}%), leaves ${fmtSize(d.close.remaining_after)}`]);
  }
  const r5 = d.rule5_stop_vs_liq as Record<string, any> | undefined;
  if (r5) {
    const ok = String(r5.verdict ?? '').includes('OK') || String(r5.verdict ?? '').includes('PASS');
    rows.push(['RULE 5 stop vs liq', `<span class="${ok ? 'pos' : 'warn'}">${esc(r5.verdict)}</span> <span class="muted">${esc(r5.liq_source ?? '')}</span>`]);
  }
  const r3 = d.rule3_risk_to_stop as Record<string, any> | undefined;
  if (r3) {
    const skipped = String(r3.verdict ?? '').startsWith('SKIPPED');
    rows.push(['RULE 3 risk to stop', `<span class="${skipped ? 'muted' : 'warn'}">${esc(r3.verdict)}</span> ${esc(r3.note ?? '')}`]);
  }
  const prior = d.prior_stop_preview as Record<string, any> | undefined;
  if (prior) {
    const list = (prior.would_cancel ?? []) as Record<string, any>[];
    rows.push([
      'prior stop',
      list.length
        ? `would cancel ${list.map((x) => `#${x.order_id} @${fmtPx(x.stop_px)}`).join(', ')}`
        : `<span class="muted">${esc(prior.note ?? 'nothing to cancel')}</span>`,
    ]);
  }
  const kvHtml = rows.map(([k, v]) => `<span>${esc(k)}</span><span>${v}</span>`).join('');
  // THE EXECUTE BANNER REPORTS THE SERVER'S RE-READ, NOT THE CLI'S FLAG (R2-7 / R2-5).
  // It used to print an unconditional "SENT to the exchange. Re-reading the book — the
  // server's own `confirmed` flag is a self-report, not proof", which is a promise about
  // work happening SOMEWHERE ELSE: the re-read it named was a separate `tpLoad(true)`
  // that begins `if (!accountHealth?.ok) return;` and can decline in silence. So on the
  // paths that CLOSE a position and CANCEL an order, the green sentence could stand over
  // zero confirmation. `exec_tools.cli.readback` now runs on every CLI write path and
  // `execute` forwards its verdict, so the sentence reports what the book actually said.
  const rb = run.readback;
  const sent =
    rb == null
      ? // No read-back in the payload at all: an older server, or a path that did not
        // write. That is UNKNOWN — never the confident green, which is the whole defect.
        `<div class="tp-note warn">SENT — but this response carried no re-read of the book,
         so the result is UNKNOWN, not confirmed. Re-read the order graph.</div>`
      : rb.verdict === 'contradicted'
        ? `<div class="tp-note bad">CONTRADICTED — the write was reported as sent and the
           exchange's book does NOT agree. ${esc(rb.note ?? '')} Read the order graph
           before acting; nothing here is a confirmation.</div>`
        : rb.verdict === 'unknown'
          ? `<div class="tp-note warn">SENT — but the book could NOT be re-read, so the
             result is UNKNOWN, not clean. ${esc(rb.error ?? rb.note ?? '')}</div>`
          : `<div class="tp-note pos">SENT, and the exchange's own book confirms it.
             ${esc(rb.note ?? '')}</div>`;
  const banner = run.refused
    ? `<div class="tp-note bad">REFUSED — ${esc(run.refused)}</div>`
    : run.phase === 'execute'
      ? run.confirmed
        ? sent
        : `<div class="tp-note warn">No confirmation was reported. Verify the book before retrying.</div>`
      : `<div class="tp-note">Nothing has been sent yet. Review the verdicts, then confirm.</div>`;
  return `${banner}<div class="tp-kv">${kvHtml}</div>`;
}

/** Is the write's own read-back evidence of anything, and which of the three states?
 *
 *  R2-5: the server re-read `/info` IN THE SAME RESPONSE and the panel ignored it,
 *  proving writes instead with a separate `tpLoad(true)` that returns silently when
 *  `accountHealth.ok` is false. Two sources of truth for one fact, and the UI used the
 *  fragile one — so a green "SENT to the exchange" could sit over zero confirmation.
 *
 *  A pure function so the sentence can be tested without a DOM, and so the three states
 *  cannot be collapsed by a later edit: `unknown` must never render as either claim
 *  (§9c), which is the whole reason `ok: false` is not simply falsy-checked.
 */
export function readbackState(run: ModifyRun): 'confirmed' | 'unknown' | 'naked' {
  if (run.naked?.critical === true) return 'naked';
  if (run.readback?.ok !== true) return 'unknown';
  if (run.naked?.present === null || run.naked?.present === undefined) return 'unknown';
  return run.naked.present ? 'confirmed' : 'naked';
}

/** The banner for a ONE-REQUEST stop/TP move. The evidence is `renderReadback`.
 *
 *  Deliberately thin: the RULE verdicts a modify can produce are all REFUSALS (RULE 5,
 *  RULE 3, RULE 11 each raise), so on the success path there is nothing to print that
 *  the venue's own read-back does not say better. What this adds is the one fact the
 *  read-back cannot: whether the price we SENT was rounded on the way out.
 *
 *  A refusal prints the server's sentence verbatim — those sentences carry the measured
 *  history (the liq buffer, the threshold) and a re-worded "action failed" would strip
 *  the evidence out of the warning. */
export function renderModifyRun(run: ModifyRun): string {
  if (run.refused) {
    // "The book was NOT changed" is FALSE in exactly the case that matters: the venue
    // offers no atomic amend for a position-TPSL leg, so cancel-ok / create-fail is
    // reachable and leaves the position unprotected on that side. The server derives
    // that from a re-read (`naked`), so the claim is now made only when the leg was
    // OBSERVED still resting — never inferred from the mere presence of a refusal.
    const n = run.naked;
    const state =
      n?.critical === true
        ? `<div class="tp-note bad">CRITICAL — ${esc(n.note ?? '')}</div>`
        : n?.present === true
          ? `<div class="tp-note">The book was NOT changed — ${esc(n.note ?? '')}</div>`
          : `<div class="tp-note warn">${esc(
              n?.note ?? 'Whether the book changed could not be established. Re-read it.',
            )}</div>`;
    return `<div class="tp-note bad">REFUSED — ${esc(run.refused)}</div>${state}`;
  }
  const rounded =
    run.price !== run.requested_price
      ? `<div class="tp-note">Sent ${fmtPx(run.price)} — the venue's own precision applied
         to ${fmtPx(run.requested_price)}.</div>`
      : '';
  if (!run.confirmed) {
    return `<div class="tp-note warn">No confirmation was reported. Verify the book before
       retrying.</div>${rounded}`;
  }
  // THE BANNER IS DERIVED FROM THE SERVER'S RE-READ, NOT FROM `confirmed` (R2-5 /
  // R4-15). It used to promise "Reading the book back" as a thing about to happen
  // elsewhere — a banner advertising a read-back that THIS path does not draw — while
  // the actual proof depended on a second request that can decline in silence. The
  // server's re-read is in this very object, so the sentence now reports what it said.
  const state = readbackState(run);
  const banner =
    state === 'naked'
      ? `<div class="tp-note bad">CRITICAL — the write was reported as sent, but the book
         does not show the ${esc(run.naked?.leg ?? '')} leg resting.
         ${esc(run.naked?.note ?? '')}</div>`
      : state === 'unknown'
        ? `<div class="tp-note warn">SENT — but the book could NOT be re-read, so the state
           of this position is UNKNOWN, not clean.
           ${esc(run.readback?.error ?? run.readback?.note ?? run.naked?.note ?? '')}</div>`
        : `<div class="tp-note pos">SENT, and the exchange's own book confirms a
           ${esc(run.naked?.leg ?? '')} leg is resting.</div>`;
  return `${banner}${rounded}`;
}

/** Panel body for the active tab.
 *
 *  THE TICKET IS NOT HERE AND IS NOT A TAB (2026-08-18, card t_ab054079): it renders
 *  from its own editable state into `#tk-mount` in the right column, permanently. There
 *  is no `null` hand-off left for it — `TabId` no longer contains `ticket`, so a
 *  reference to it is a compile error rather than a silently dead branch. */
export function renderTab(tab: TabId, snap: Snapshot | null, health: AccountHealth | null,
                          selected: string | null, sort: SortState = null,
                          cancelling: CancelStates = new Map()): string | null {
  // SETUPS renders from `/api/setups`, not from the account snapshot, so main.ts
  // handles it before delegating here — same hand-off as the ticket, and null is
  // what makes that hand-off explicit rather than forgettable.
  if (tab === 'setups') return null;
  // ADVICE renders from `/api/manage/advice` crossed with the snapshot, so main.ts
  // handles it before delegating here — same hand-off as setups and the ticket.
  if (tab === 'advice') return null;
  // WALLET renders from the credential endpoint, not from the account snapshot — and it
  // The wallet form used to be handled here, before the `!snap` guard, because gating it on a
  // snapshot that cannot exist without a wallet made the fix unreachable behind the error it fixes.
  // It now lives in the profile modal, which sidesteps that circularity entirely: the modal never
  // depends on an account snapshot.
  if (!snap) return emptyState('No data yet', 'Loading the account snapshot…');
  switch (tab) {
    case 'positions': return renderPositions(snap, selected, sort);
    case 'orders': return renderOrders(snap, selected, sort, cancelling);
    case 'fills': return renderFills(snap, sort);
    case 'funding': return renderFunding(snap, sort);
    case 'balances': return renderBalances(snap);
    case 'findings': return renderFindings(snap, health, sort);
  }
}

/** Per-tab counts for the tab bar, exactly as the HL UI badges them.
 *
 *  `ticket: null` renders no badge — a count on a form would be meaningless, and
 *  `setups: null` for the same reason from the other direction: its rows come from
 *  the scan, not from this snapshot, so this function has nothing to count. The
 *  count is supplied by the caller that owns the payload. */
export function tabCounts(snap: Snapshot | null): Record<TabId, number | null> {
  if (!snap) {
    return { positions: null, orders: null, fills: null,
             funding: null, balances: null, findings: null, setups: null, advice: null };
  }
  return {
    positions: snap.positions.length,
    orders: snap.orders.length,
    fills: snap.fills.length,
    funding: snap.funding.length,
    balances: snap.balances.pools.filter((p) => p.account_value > 0).length,
    findings: snap.findings.length,
    setups: null,
    advice: null,
  };
}

/** The worst severity present, for the tab-bar status dot. */
export function worstSeverity(snap: Snapshot | null): 'CRITICAL' | 'WARN' | 'INFO' | 'CLEAN' {
  if (!snap) return 'CLEAN';
  if (snap.exit_code === 2) return 'CRITICAL';
  if (snap.exit_code === 1) return 'WARN';
  return snap.findings.length ? 'INFO' : 'CLEAN';
}
